import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Formik, Field, Form } from 'formik';
import { withTranslation } from 'react-i18next';
import { Prompt } from 'react-router';
import {
  VerticalSpacer,
  Checkbox,
  TextInput,
  Button
} from '@01_people/ui-library';
import {
  SUBTYPE_ID_CARD,
  SUBTYPE_DRIVER_LIC,
  SUBTYPE_PASSPORT,
  TYPE_IDENTITY,
  TYPE_IDENTITY_OLD
} from 'utils/constants';
import SvgIcon from 'components/svg-icon';
import DocumentSelector from '../document-selector';
import DocumentUploader from '../document-uploader';
import POISchema from './proof-of-identity.schema';
import { removeDuplicateValues } from '../utils';
import * as ActionCreators from '../../../redux/action';
import ProofOfIdentitySC from './proof-of-identity.styled';
import iconIdCard from '../../../assets/svg/id-card-icon.svg';
import DocumentStatusInfo from '../status-info';

const initialFromValues = {
  typeOfDocument: SUBTYPE_ID_CARD,
  dayOfExpiration: '',
  monthOfExpiration: '',
  yearOfExpiration: '',
  withoutExpiration: false,
  idNumber: '',
  idDocument: []
};

const ProofOfIndentity = ({
  active,
  setActive,
  setInactive,
  subtypes,
  documents,
  devices,
  uploadKycDocument,
  setWithoutExpirationDate,
  t,
  showOnly
}) => {
  const currentDocument = documents
    ? documents.find(
        d => d.type === TYPE_IDENTITY || TYPE_IDENTITY_OLD.includes(d.type)
      )
    : null;
  const currentStatus = currentDocument ? currentDocument.status : 'unUploaded';

  /**
   * Submit data to server
   * @param {object} values values from form
   */
  const onFormSubmit = values => {
    uploadKycDocument({
      ...values,
      typeOfDocumentId: subtypes[values.typeOfDocument].id
    });
  };

  const getHeading = value => {
    switch (value) {
      case SUBTYPE_ID_CARD:
        return t('kyc.identity.form.fieldHeadings.idc');
      case SUBTYPE_DRIVER_LIC:
        return t('kyc.identity.form.fieldHeadings.drl');
      case SUBTYPE_PASSPORT:
        return t('kyc.identity.form.fieldHeadings.pst');
      default:
        return '';
    }
  };

  const getIdNumberPlaceholder = selectedDocument => {
    return `${t('kyc.identity.form.numberPlaceholder')} ${t(
      `kyc.documentsLabels.${selectedDocument}`
    )}`;
  };

  const handleCheckboxChange = val => setWithoutExpirationDate(val);

  const renderExpirationErrors = (errors, touched, withoutExpiration) => {
    if (withoutExpiration) return null;
    removeDuplicateValues(errors);

    if (
      touched.dayOfExpiration ||
      touched.monthOfExpiration ||
      touched.yearOfExpiration
    ) {
      if (
        errors.dayOfExpiration === undefined &&
        errors.monthOfExpiration === undefined &&
        errors.yearOfExpiration === undefined &&
        errors.dateError === undefined
      ) {
        return null;
      }

      if (
        // all of them has same error
        typeof errors.dayOfExpiration === 'string' &&
        errors.dayOfExpiration === errors.monthOfExpiration &&
        errors.monthOfExpiration === errors.yearOfExpiration
      ) {
        return (
          <div className="text-error">
            <p>{t(errors.dayOfExpiration)}</p>
          </div>
        );
      }
      return (
        <div className="text-error">
          <p>{t(errors.dayOfExpiration)}</p>
          <p>{t(errors.monthOfExpiration)}</p>
          <p>{t(errors.yearOfExpiration)}</p>
          <p>{t(errors.dateError)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <ProofOfIdentitySC>
      <div className="container">
        <div className="kyc-inner-block">
          {currentStatus === 'unUploaded' ||
          currentStatus === 'rejectedByUser' ? (
            <>
              {active ? (
                <Formik
                  enableReinitialize
                  initialValues={initialFromValues}
                  validationSchema={POISchema}
                  onSubmit={onFormSubmit}
                  render={({
                    values,
                    setFieldValue,
                    setFieldTouched,
                    errors,
                    touched,
                    isSubmitting,
                    resetForm,
                    dirty
                  }) => (
                    <Form>
                      <Prompt when={dirty} message={t('kyc.leaveMessage')} />
                      <div className="row">
                        <div className="col title-col">
                          <div className="row">
                            <SvgIcon
                              icon={iconIdCard}
                              alt="identity_icon"
                              themed
                              active
                              styling="primary"
                            />
                            <h5 className="block-title">
                              {t('kyc.identity.title')}
                            </h5>
                          </div>
                        </div>
                      </div>
                      <VerticalSpacer height="8px" />
                      <Field
                        name="typeOfDocument"
                        component={DocumentSelector}
                        type={TYPE_IDENTITY}
                        value={values.typeOfDocument}
                        onChange={document => {
                          resetForm(initialFromValues);
                          setFieldValue('typeOfDocument', document);
                        }}
                        errors={
                          errors.typeOfDocument && touched.typeOfDocument
                            ? [t(errors.typeOfDocument)]
                            : []
                        }
                      />
                      <VerticalSpacer height="40px" />
                      <h6>{t('kyc.identity.heading1')}</h6>
                      <VerticalSpacer height="8px" />
                      <Field
                        type="text"
                        name="idNumber"
                        component={TextInput}
                        label={getIdNumberPlaceholder(values.typeOfDocument)}
                        customClass="number-id"
                        defaultValue={values.idNumber}
                        onChange={value => {
                          setFieldValue('idNumber', value);
                        }}
                        onBlur={() => setFieldTouched('idNumber', true)}
                        setTouched={value => setFieldTouched('idNumber', value)}
                        renderErrors
                        errors={
                          errors.idNumber && touched.idNumber
                            ? [t(errors.idNumber)]
                            : []
                        }
                      />

                      <span className="section-small-heading">
                        {t('kyc.identity.form.expirationDate.title')}
                      </span>

                      <div className="expiration">
                        <Field
                          type="text"
                          name="dayOfExpiration"
                          customClass="without-mb-20"
                          label={t('kyc.identity.form.expirationDate.day')}
                          component={TextInput}
                          defaultValue={values.dayOfExpiration}
                          onChange={value =>
                            setFieldValue('dayOfExpiration', value)
                          }
                          onBlur={() =>
                            setFieldTouched('dayOfExpiration', true)
                          }
                          setTouched={value =>
                            setFieldTouched('dayOfExpiration', value)
                          }
                          renderErrors={false}
                          errors={
                            (errors.dayOfExpiration || errors.dateError) &&
                            (touched.dayOfExpiration ||
                              touched.monthOfExpiration ||
                              touched.yearOfExpiration)
                              ? [t(errors.dayOfExpiration)]
                              : []
                          }
                        />
                        <Field
                          type="text"
                          name="monthOfExpiration"
                          customClass="without-mb-20"
                          label={t('kyc.identity.form.expirationDate.month')}
                          component={TextInput}
                          defaultValue={values.monthOfExpiration}
                          onChange={value =>
                            setFieldValue('monthOfExpiration', value)
                          }
                          onBlur={() =>
                            setFieldTouched('monthOfExpiration', true)
                          }
                          setTouched={value =>
                            setFieldTouched('monthOfExpiration', value)
                          }
                          renderErrors={false}
                          errors={
                            (errors.monthOfExpiration || errors.dateError) &&
                            (touched.monthOfExpiration ||
                              touched.yearOfExpiration ||
                              touched.dayOfExpiration)
                              ? [t(errors.monthOfExpiration)]
                              : []
                          }
                        />
                        <Field
                          type="text"
                          name="yearOfExpiration"
                          customClass="without-mb-20"
                          label={t('kyc.identity.form.expirationDate.year')}
                          component={TextInput}
                          defaultValue={values.yearOfExpiration}
                          onChange={value =>
                            setFieldValue('yearOfExpiration', value)
                          }
                          onBlur={() =>
                            setFieldTouched('yearOfExpiration', true)
                          }
                          setTouched={value =>
                            setFieldTouched('yearOfExpiration', value)
                          }
                          renderErrors={false}
                          errors={
                            (errors.yearOfExpiration || errors.dateError) &&
                            (touched.yearOfExpiration ||
                              touched.monthOfExpiration ||
                              touched.dayOfExpiration)
                              ? [
                                  t(errors.yearOfExpiration),
                                  t(errors.dateError)
                                ]
                              : []
                          }
                        />
                      </div>
                      {renderExpirationErrors(
                        errors,
                        touched,
                        values.withoutExpiration
                      )}
                      <VerticalSpacer height="16px" />
                      <Field
                        type="checkbox"
                        name="withoutExpiration"
                        component={Checkbox}
                        label={t('kyc.identity.form.noExpiration')}
                        defaultValue={values.withoutExpiration}
                        onChange={value => {
                          setFieldValue('withoutExpiration', value);
                          handleCheckboxChange(value);
                        }}
                        onBlur={() => setFieldTouched('yearOfExpiration', true)}
                        setTouched={value =>
                          setFieldTouched('withoutExpiration', value)
                        }
                        renderErrors={false}
                      />

                      <VerticalSpacer height="40px" />
                      <Field
                        component={DocumentUploader}
                        name="idDocument"
                        value={values.idDocument}
                        allowMultipleFiles={
                          values.typeOfDocument !== SUBTYPE_PASSPORT
                        }
                        devices={devices}
                        onChange={files => {
                          setFieldValue('idDocument', files);
                        }}
                        onTouch={fieldTouched => {
                          setFieldTouched('idDocument', fieldTouched);
                        }}
                        errors={[t(errors.idDocument)]}
                        touched={touched.idDocument}
                        maxFiles={
                          values.typeOfDocument === SUBTYPE_DRIVER_LIC ||
                          values.typeOfDocument === SUBTYPE_ID_CARD
                            ? 2
                            : 1
                        }
                        label=""
                        subLabel={getHeading(values.typeOfDocument)}
                      />
                      <div className="save-wrapper">
                        <Button
                          customClass="close-btn"
                          styling="secondary"
                          label={t('kyc.closeButton')}
                          onClick={() => setInactive()}
                        />
                        <Button
                          styling="primary"
                          type="submit"
                          label={t('kyc.saveButton')}
                        />
                      </div>
                    </Form>
                  )}
                />
              ) : (
                <div className="title-row">
                  <div className="col title-col">
                    <div className="row">
                      <SvgIcon
                        icon={iconIdCard}
                        alt="identity_icon"
                        themed
                        active
                        styling="primary"
                      />
                      <h5 className="block-title">{t('kyc.identity.title')}</h5>
                    </div>
                  </div>
                  <div className="col title-col">
                    {showOnly ? null : (
                      <Button
                        styling="secondary"
                        label={t('kyc.editButton')}
                        onClick={() => setActive()}
                      />
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <DocumentStatusInfo
              status={currentStatus}
              currentDocument={currentDocument}
            />
          )}
        </div>
      </div>
    </ProofOfIdentitySC>
  );
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      uploadKycDocument: ActionCreators.uploadKycDocument,
      toggleInfoModal: ActionCreators.toggleInfoModal
    },
    dispatch
  );

export default withTranslation('pages')(
  connect(
    state => ({
      devices: state.devices,
      approved: state.user.kyc.approvedIdentity,
      documents: state.user.kyc.documents,
      subtypes: state.user.kyc.subtypes,
      responsiveContentView: state.ui.responsiveContent
    }),
    mapDispatchToProps
  )(ProofOfIndentity)
);
